/**
 * نوارِ کشف یادش می‌ماند کجا بودی (WHOSUP-232).
 *
 * روی گوشی صفحه‌ی کشف یک تبه و با باز کردنِ یک رخداد از درخت بیرون نمی‌رود:
 * برمی‌گردی و جستجو و دسته و فیلترِ زمان همان‌جا هستند. روی وب هر رفتن به
 * `/event/:id` این صفحه را unmount می‌کند و برگشت یک mountِ کاملاً تازه است،
 * پس هرچه تایپ شده بود می‌رفت — و کاربر باید از اولِ فهرست دوباره می‌گشت.
 *
 * پس همان چیزی که آن‌جا توی حافظه‌ی صفحه می‌ماند، این‌جا توی `sessionStorage`
 * می‌ماند:
 *  - **sessionStorage و نه localStorage**، چون این حالتِ «همین نشستِ گشتن»
 *    است نه یک ترجیح؛ تبی که بسته شود، جستجویش هم باید برود. (شهرِ انتخابی
 *    برعکس است و عمداً localStorage دارد — نگاه کن به cityScope.)
 *  - **و نه یک متغیرِ ماژول**، چون رفرشِ صفحه‌ی رخداد یا باز کردنش با آدرسِ
 *    مستقیم هم باید به همین فهرست برگردد.
 *  - **و نه پارامترِ آدرس**، چون هر حرفِ تایپ‌شده یک ردیفِ history می‌شد و
 *    دکمه‌ی «برگرد» را بی‌مصرف می‌کرد.
 *
 * چیزی که ذخیره می‌شود از یک نسخه‌ی قدیمی‌ترِ همین اپ آمده و ممکن است شکلش
 * عوض شده باشد، پس موقعِ خواندن **هر فیلد جدا اعتبارسنجی می‌شود**: یک مقدارِ
 * ناجور نباید صفحه را بترکاند، فقط باید نادیده گرفته شود.
 */
import {
  DAY_PARTS, EMPTY_WHEN, WHEN_PRESETS, type DayPart, type WhenFilter,
} from '@/core/lib/discoverWhen';
import { SORT_KEY } from '@/core/lib/discoverSort';
import type { DiscoverSort } from '@/core/lib/prefs';

/** همان چیزهایی که نوارِ بالای صفحه‌ی کشف تنظیمشان می‌کند. */
export interface DiscoverFilters {
  q: string;
  tagId: number | null;
  when: WhenFilter;
  sort: DiscoverSort;
  kinds: { events: boolean; places: boolean };
  /** «بیشتر»ِ ردیفِ دسته‌ها باز بوده یا نه. */
  allCats: boolean;
}

const EMPTY_FILTERS: DiscoverFilters = {
  q: '',
  tagId: null,
  when: EMPTY_WHEN,
  sort: 'nextTime',
  kinds: { events: true, places: true },
  allCats: false,
};

const KEY = 'whosup.discover.v1';

/** خواندن/نوشتنِ حافظه‌ی مرورگر توی حالتِ ناشناس می‌تواند throw کند. */
const store = (): Storage | null => {
  try { return window.sessionStorage; } catch { return null; }
};

const isWhen = (v: unknown): v is WhenFilter => {
  if (!v || typeof v !== 'object') return false;
  const w = v as Record<string, unknown>;
  if (w.preset !== null && !WHEN_PRESETS.includes(w.preset as never)) return false;
  if (w.date !== null && typeof w.date !== 'string') return false;
  return Array.isArray(w.parts)
    && w.parts.every((p) => DAY_PARTS.includes(p as DayPart));
};

/** آخرین حالتِ نوار، یا حالتِ خالی اگر چیزی ذخیره نشده (یا ناجور بوده). */
export function readFilters(): DiscoverFilters {
  const raw = store()?.getItem(KEY);
  if (!raw) return EMPTY_FILTERS;
  try {
    const v = JSON.parse(raw) as Record<string, unknown>;
    const kinds = v.kinds as Record<string, unknown> | undefined;
    return {
      q: typeof v.q === 'string' ? v.q : '',
      tagId: typeof v.tagId === 'number' ? v.tagId : null,
      when: isWhen(v.when) ? v.when : EMPTY_WHEN,
      // ترتیب‌ها اسمشان کلیدِ `SORT_KEY` است، پس همان فهرست اعتبارسنجی هم هست.
      sort: typeof v.sort === 'string' && v.sort in SORT_KEY
        ? v.sort as DiscoverSort : 'nextTime',
      kinds: {
        events: kinds?.events !== false,
        places: kinds?.places !== false,
      },
      allCats: v.allCats === true,
    };
  } catch {
    return EMPTY_FILTERS;
  }
}

export function writeFilters(f: DiscoverFilters): void {
  try { store()?.setItem(KEY, JSON.stringify(f)); } catch { /* پر بودنِ حافظه */ }
}
